<template>
  <Scaffold :loading="loading">
    <template #toolbar>
      <ts-toolbar class="toolbar">
        <div class="top-row">
          <div class="title">My projects</div>
          <ts-search-bar
            size="mini"
            placeholder="Search"
            class="search-bar"
            :value="filters.query"
            @input="handleQueryChange"
          ></ts-search-bar>
          <visible :when="isProjectManager" class="flex">
            <ts-button
              v-if="isAdmin"
              class="mr-4"
              :loading="exporting"
              size="small"
              filled
              icon="el-icon-document"
              @click="handleProjectsExport"
            >
              Export
            </ts-button>
            <ts-button
              ref="createButton"
              type="normal"
              size="small"
              filled
              class="mr-4"
              icon="el-icon-plus"
              @click="handleCreateFolderClick"
            >
              Create new folder
            </ts-button>
            <ts-button type="primary" size="small" filled icon="el-icon-plus" @click="handleCreateProjectClick">
              Create new project
            </ts-button>
          </visible>
        </div>
        <div class="bottom-row">
          <MultiSelectionDropdown
            placeholder="Status"
            :width="234"
            :options="statuses"
            :value="filters.statuses"
            @input="handleFilterChange('statuses', $event)"
          ></MultiSelectionDropdown>
          <MultiSelectionDropdown
            placeholder="Brands"
            text-filter
            show-picture
            :width="234"
            :options="brands"
            :value="filters.brands"
            :read-only-single-option="false"
            @input="handleFilterChange('brands', $event)"
          ></MultiSelectionDropdown>
          <MultiSelectionDropdown
            placeholder="Users"
            text-filter
            :width="234"
            :options="users"
            :value="filters.users"
            :read-only-single-option="false"
            @input="handleFilterChange('users', $event)"
          ></MultiSelectionDropdown>
          <DatePickerDropdown
            placeholder="Due date"
            mode="single"
            :width="234"
            :value="filters.dueDate"
            @input="handleFilterChange('dueDate', $event)"
          ></DatePickerDropdown>
        </div>
      </ts-toolbar>
    </template>
    <div class="list">
      <folder v-for="folder in projects" :key="folder.id" :folder="folder"></folder>
    </div>
    <create-folder />
    <prevent-folder-archive-dialog />
    <move-projects />
  </Scaffold>
</template>

<style lang="scss" scoped>
.mr-4 {
  margin-right: 1rem;
}
.toolbar {
  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      font-family: Roboto;
      font-size: 16px;
      font-weight: 500;
      color: #6d7587;
    }

    .search-bar {
      width: 500px;
    }
  }

  .bottom-row {
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
  }
}

.list {
  padding: 20px 40px;

  .row {
    background: #ebecf0;
    background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
    background-blend-mode: soft-light, normal;
    background-clip: padding-box;
    border: 2px solid rgba(255, 255, 255, 0.16);
    box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
    border-radius: 16px;

    display: flex;
    margin-bottom: 12px;

    .cell {
      display: flex;
      flex-direction: column;
      align-content: center;
      justify-content: center;
      padding: 12px;

      .centered {
        text-align: center;
      }

      .steps-statuses {
        font-family: Roboto;
        font-size: 8px;
        color: #9aa5b7;

        .step-status {
          display: flex;
          align-items: flex-end;
          margin: 2px 0;

          .badge {
            margin-right: 3px;
          }

          a {
            font-family: Roboto;
            font-size: 8px;
            color: #9aa5b7;
            text-decoration: none;
          }
        }
      }

      .project-name {
        font-family: Roboto;
        font-size: 12px;
        font-weight: 500;
        line-height: 20px;
        color: #000000;
      }

      .project-link {
        font-family: Roboto;
        font-size: 12px;
        font-weight: 500;
        color: #000000;
        text-decoration: none;
      }

      .project-brands {
        font-family: Roboto;
        font-size: 12px;
        color: #6d7587;
      }

      .project-quote-number {
        font-family: Roboto;
        font-size: 8px;
        color: #9aa5b7;
      }

      .due-date {
        font-family: Roboto;
        font-size: 12px;
        text-align: center;
        line-height: 50px;
        display: block;
        color: #6d7587;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        > * {
          margin-left: 12px;
        }
      }
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex';

import Scaffold from '@/components/layouts/Scaffold.vue';
import MultiSelectionDropdown from '@/components/inputs/MultiSelectionDropdown.vue';
import DatePickerDropdown from '@/components/inputs/DatePickerDropdown.vue';
import ProjectStatusSelect from '@/components/inputs/ProjectStatusSelect.vue';
import UserPicker from '@/components/inputs/UserPicker.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';

import Pen from '@/assets/pen.svg';
import Trash from '@/components/the-source-ui/icons/Trash.vue';
import Folder from './Folders/Folder.vue';
import CreateFolder from './Folders/CreateFolder.vue';
import PreventFolderArchiveDialog from './Folders/PreventFolderArchiveDialog.vue';
import MoveProjects from './Folders/MoveProjects.vue';

export default {
  mixins: [projectViewHelpers],
  components: {
    Scaffold,
    DatePickerDropdown,
    MultiSelectionDropdown,
    ProjectStatusSelect,
    UserPicker,
    Pen,
    Trash,
    Folder,
    CreateFolder,
    PreventFolderArchiveDialog,
    MoveProjects
  },
  computed: {
    ...mapState('workflow', {
      loading: state => state.myProjects.loading,
      error: state => state.myProjects.error,
      projects: state => state.myProjects.items,
      filters: state => state.myProjects.filters,
      statuses: state => state.statuses.items,
      brands: state => state.myProjects.brands.items,
      users: state => state.myProjects.users.items,
      groups: state => state.myProjects.options.groups
    }),
    columnDefinitions() {
      return [
        {
          id: 'status',
          name: 'Status',
          width: 140
        },
        {
          id: 'steps-statuses',
          name: 'Steps statuses',
          width: 100
        },
        {
          id: 'name',
          name: 'Name',
          width: 250
        },
        {
          id: 'due-date',
          name: 'Due date',
          width: 140
        },
        {
          id: 'users',
          name: 'Users',
          width: 200
        },
        {
          id: 'actions',
          name: 'Actions',
          width: 200
        }
      ];
    }
  },
  methods: {
    ...mapActions('workflow', [
      'fetchBrands',
      'fetchUsers',
      'fetchMyProjects',
      'updateMyProjectsQuery',
      'updateMyProjectsFilter',
      'updateProjectStatus',
      'deleteProject',
      'exportProjects',
      'fetchProjectOptions',
      'updateProjectUsers'
    ]),
    handleCreateFolderClick() {
      window.EventBus.$emit('folders.create', (folder) => {
        if (folder) {
          window.location.reload();
        }
      });
    },
    getStepStatus(project, step) {
      const statusDetail = project.statusDetails[step];
      switch (statusDetail) {
        case 'pending':
          return 'error';
        case 'done':
          return 'success';
        default:
          console.error(`Status not found for project ${project.id}: ${step} (was ${statusDetail}`);
          return 'neutral';
      }
    },
    handleQueryChange(query) {
      this.updateMyProjectsQuery({ query });
    },
    handleFilterChange(field, value) {
      this.updateMyProjectsFilter({ field, value });
    },
    handleProjectStatusChange(project, value) {
      this.updateProjectStatus({ project, value });
    },
    handleDeleteClick(project) {
      if (!confirm('Delete this project?')) {
        return;
      }

      this.deleteProject({ project });
    },
    handleProjectUsersChange(project, value) {
      this.updateProjectUsers({ project, value });
    },
    handleProjectsExport() {
      this.exporting = true;
      this.exportProjects()
        .then(data => {
          var fileURL = window.URL.createObjectURL(new Blob([data.file]));
          var fileLink = document.createElement('a');

          fileLink.href = fileURL;
          fileLink.setAttribute('download', data.filename);
          document.body.appendChild(fileLink);

          fileLink.click();
          this.exporting = false;
        })
        .catch(error => (this.exporting = false));
    },
    handleCreateProjectClick() {
      this.$router.push({ name: 'create-project' });
    },
    handleEditClick(project) {
      this.$router.push({ name: 'project-details-general-info', params: { projectId: project.id } });
    }
  },
  mounted() {
    this.fetchBrands();
    this.fetchUsers();
    this.fetchMyProjects();
    this.fetchProjectOptions();
  },
  data() {
    return {
      exporting: false
    };
  },
  watch: {
    error(value) {
      if (value) {
        this.showError(value);
      }
    }
  }
};
</script>
